export type Pillar2FilingContentFormat = 'json' | 'xml';

export function isXmlContent(content: string): boolean {
    const trimmed = content.trimStart();
    return trimmed.startsWith('<?xml') || trimmed.startsWith('<');
}

export function detectContentFormat(content: string): Pillar2FilingContentFormat {
    return isXmlContent(content) ? 'xml' : 'json';
}

export function getContentMimeType(format: Pillar2FilingContentFormat): string {
    return format === 'xml' ? 'application/xml' : 'application/json';
}

export function getContentFileExtension(format: Pillar2FilingContentFormat): string {
    return format === 'xml' ? 'xml' : 'json';
}

export function formatJson(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

/** Formats filing payload/response for display and download; preserves string content (e.g. XML). */
export function formatFilingContent(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    return formatJson(value);
}

export function extractOperationContent(data: unknown): string {
    if (data === null || data === undefined) {
        return '';
    }
    if (typeof data === 'string') {
        return data;
    }
    if (typeof data === 'object') {
        const record = data as Record<string, unknown>;
        const content = record.content ?? record.Content;
        if (typeof content === 'string') {
            return content;
        }
        if (content !== null && content !== undefined) {
            return formatJson(content);
        }
    }
    return '';
}

/** Builds a download base name (no extension), optionally prefixed with a sanitized return name. */
export function buildPillar2FilingDownloadFilename(basePrefix: string, returnName?: string | null): string {
    const trimmed = returnName?.trim();
    if (!trimmed) {
        return basePrefix;
    }
    const safeReturnName = trimmed.replace(/[\\/:*?"<>|]/g, '-');
    return `${basePrefix}-${safeReturnName}`;
}

export function downloadFilingContentFile(baseFilename: string, content: string): void {
    if (!content) {
        return;
    }
    const format = detectContentFormat(content);
    const filename = `${baseFilename}.${getContentFileExtension(format)}`;
    const blob = new Blob([content], { type: getContentMimeType(format) });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
}
